import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Col, Row } from '../../lib/utils';
import { Body2Bold, Body3Regular, Body3Bold, CaptionRegular, Head4 } from '../../lib/font';
import { colors } from '../../lib/colors';
import { getPaymentRequest, formatPrice } from './data/dummyData';

const MockPGPage: React.FC = () => {
    const { paymentRequestId } = useParams<{ paymentRequestId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get('type') || 'payment';

    const isBilling = type === 'billing';
    const data = getPaymentRequest(paymentRequestId || '');

    const [selectedMethod, setSelectedMethod] = useState('카드');
    const [isAgreementChecked, setIsAgreementChecked] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const paymentMethods = isBilling ? ['카드'] : ['카드', '간편결제'];
    const isFormValid = !!selectedMethod && isAgreementChecked;

    const handleSubmit = () => {
        if (!isFormValid || isProcessing) return;
        setIsProcessing(true);

        setTimeout(() => {
            navigate(`/pay/complete?type=${type}&requestId=${paymentRequestId}`);
        }, 2000);
    };

    const handleCancel = () => {
        navigate(-1);
    };

    if (isProcessing) {
        return (
            <Col style={{ width: '100%', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFFFF' }}>
                <Col style={{ alignItems: 'center', gap: 16 }}>
                    <div
                        style={{
                            width: 48,
                            height: 48,
                            border: `4px solid ${colors.GREY_200}`,
                            borderTop: `4px solid ${colors.primary}`,
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                        }}
                    />
                    <Body2Bold style={{ color: colors.LAVEL_4 }}>
                        {isBilling ? '카드를 등록하고 있습니다...' : '결제를 진행하고 있습니다...'}
                    </Body2Bold>
                    <CaptionRegular style={{ color: colors.LAVEL_2 }}>잠시만 기다려 주세요</CaptionRegular>
                </Col>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </Col>
        );
    }

    return (
        <Col style={{ width: '100%', minHeight: '100vh', alignItems: 'center', backgroundColor: '#F5F7FA' }}>
            <Col style={{ width: '100%', maxWidth: 480, minHeight: '100vh', backgroundColor: '#FFFFFF' }}>
                {/* PG 헤더 */}
                <Row
                    style={{
                        width: '100%',
                        height: 52,
                        backgroundColor: '#0064FF',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 16px',
                    }}
                >
                    <Body3Bold style={{ color: '#FFFFFF' }}>토스페이먼츠 결제 모듈 Mock 화면</Body3Bold>
                </Row>

                <Col style={{ padding: '24px 20px', gap: 20 }}>
                    {/* 결제 정보 */}
                    <Col
                        style={{
                            width: '100%',
                            padding: '16px 20px',
                            backgroundColor: '#F8F9FA',
                            borderRadius: 8,
                            border: '1px solid #E9ECEF',
                        }}
                    >
                        <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            <CaptionRegular style={{ color: colors.LAVEL_2 }}>
                                {isBilling ? '카드 등록' : '결제 금액'}
                            </CaptionRegular>
                            {data && !isBilling && (
                                <Head4 style={{ color: colors.LAVEL_4, fontSize: 20 }}>
                                    {formatPrice(data.totalPrice)}원
                                </Head4>
                            )}
                            {isBilling && (
                                <Body3Bold style={{ color: colors.primary }}>자동결제 카드 등록</Body3Bold>
                            )}
                        </Row>
                        {data && (
                            <CaptionRegular style={{ color: colors.LAVEL_2, marginTop: 4 }}>
                                {data.centerName}
                            </CaptionRegular>
                        )}
                    </Col>

                    {/* 결제수단 선택 */}
                    <Col style={{ gap: 16 }}>
                        <Body3Bold style={{ color: colors.LAVEL_4 }}>결제수단</Body3Bold>
                        <Row style={{ gap: 8, flexWrap: 'wrap' }}>
                            {paymentMethods.map((method) => {
                                const isSelected = selectedMethod === method;
                                return (
                                    <button
                                        key={method}
                                        onClick={() => setSelectedMethod(method)}
                                        style={{
                                            minWidth: 96,
                                            height: 40,
                                            borderRadius: 8,
                                            border: isSelected ? '1px solid #0064FF' : '1px solid #DEE2E6',
                                            backgroundColor: isSelected ? '#F2F7FF' : '#FFFFFF',
                                            color: isSelected ? '#0064FF' : colors.LAVEL_3,
                                            padding: '0 12px',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {method}
                                    </button>
                                );
                            })}
                        </Row>
                    </Col>

                    {/* 안내 문구 */}
                    <Col
                        style={{
                            width: '100%',
                            padding: '14px 16px',
                            backgroundColor: '#F8FBFF',
                            borderRadius: 8,
                            border: '1px solid #DCEBFF',
                            gap: 6,
                        }}
                    >
                        <Body3Regular style={{ color: colors.LAVEL_4 }}>
                            {isBilling
                                ? '선택한 카드로 정기결제가 진행되며, 결제일에 자동 승인됩니다.'
                                : '결제 요청 시 토스페이먼츠 모듈 동작을 Mock으로 시뮬레이션합니다.'}
                        </Body3Regular>
                        <CaptionRegular style={{ color: colors.LAVEL_2 }}>
                            실제 카드 승인/과금은 발생하지 않습니다.
                        </CaptionRegular>
                    </Col>

                    {/* 약관 동의 */}
                    <button
                        onClick={() => setIsAgreementChecked((prev) => !prev)}
                        style={{
                            width: '100%',
                            border: '1px solid #E9ECEF',
                            borderRadius: 8,
                            backgroundColor: '#FFFFFF',
                            padding: '12px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                        }}
                    >
                        <CaptionRegular style={{ color: colors.LAVEL_4 }}>
                            [필수] 결제 진행 및 개인정보 처리에 동의합니다.
                        </CaptionRegular>
                        <Body3Bold style={{ color: isAgreementChecked ? '#0064FF' : colors.LAVEL_2 }}>
                            {isAgreementChecked ? '동의함' : '미동의'}
                        </Body3Bold>
                    </button>

                    <Col style={{ gap: 6 }}>
                        <CaptionRegular style={{ color: colors.LAVEL_2 }}>
                            주문번호: {paymentRequestId || '-'}
                        </CaptionRegular>
                        <CaptionRegular style={{ color: colors.LAVEL_2 }}>
                            결제수단: {selectedMethod}
                        </CaptionRegular>
                    </Col>
                </Col>

                {/* 하단 버튼 */}
                <Col style={{ padding: '12px 20px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))', gap: 8, marginTop: 'auto' }}>
                    <button
                        onClick={handleSubmit}
                        disabled={!isFormValid}
                        style={{
                            width: '100%',
                            height: 52,
                            borderRadius: 8,
                            border: 'none',
                            cursor: isFormValid ? 'pointer' : 'not-allowed',
                            backgroundColor: isFormValid ? '#0064FF' : colors.GREY_200,
                            color: '#FFFFFF',
                            fontSize: 16,
                            fontWeight: 700,
                        }}
                    >
                        {isBilling ? '자동결제 카드 등록 요청(mock)' : `${data ? formatPrice(data.totalPrice) : '0'}원 결제 요청(mock)`}
                    </button>
                    <button
                        onClick={handleCancel}
                        style={{
                            width: '100%',
                            height: 44,
                            borderRadius: 8,
                            border: '1px solid #DEE2E6',
                            cursor: 'pointer',
                            backgroundColor: '#FFFFFF',
                            color: colors.LAVEL_3,
                            fontSize: 14,
                            fontWeight: 500,
                        }}
                    >
                        취소
                    </button>
                    <CaptionRegular style={{ color: colors.LAVEL_1, textAlign: 'center', marginTop: 4 }}>
                        본 화면은 토스페이먼츠 심사용 mock 결제 화면입니다.
                    </CaptionRegular>
                </Col>
            </Col>
        </Col>
    );
};

export default MockPGPage;
